import {
  useMemo,
  useState,
  useEffect,
  useCallback,
  startTransition
} from 'react';

import { useNavigate } from 'react-router';
import { GroupBase, OptionsOrGroups } from 'react-select';
import { Row as RowTanstack, CellContext } from '@tanstack/react-table';

import { useAppDispatch, useAppSelector } from 'store';
import {
  Row,
  Option,
  TableKeys,
  RowWithId,
  HeaderKeys,
  QuarterKey,
  RowWithDate,
  SeparatorKeys,
  ExpansionKeys,
  OverriddenData
} from 'components/EditableTable/types';
import PortCoUserData from './PortCoUserData';
import {
  getColumns,
  mapFormulas,
  getDependents
} from 'components/EditableTable/utils/helpers';
import {
  DEPENDENCIES,
  DATE_OPTIONS,
  INITIAL_PORTCO_DATA,
  ENDING_ARR_ALL_DEPENDENCIES
} from 'components/EditableTable/utils/constants';
import { EditableTable } from 'components/EditableTable';
import VerticalAccordion from 'components/VerticalAccordion';
import {
  setisDataReviewed,
  setDefaultQuarterIdx
} from 'store/portCoData/portCoData.slice';
import { getOverrides } from 'components/EditableTable/utils/getOverrides';
import { mapOverrides } from 'components/EditableTable/utils/mapOverrides';
import { getUserHistoricalCompsData } from 'network/getUserHistoricalCompsData';
import { transformToQuarterData } from 'components/EditableTable/utils/mapPortCoData';
import { getUserHistoricalCompsDataOverrides } from 'network/getUserHistoricalCompsDataOverrides';
import { postUserHistoricalCompsDataOverrides } from 'network/postUserHistoricalCompsDataOverrides';

const PortCoUserPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const defaultQuarterIdx = useAppSelector(
    state => state.portCoData.defaultQuarterIdx
  );

  const [isReviewed, setReviewed] = useState(false);
  const [isFormDirty, setFormDirty] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [labels, setLabels] = useState<QuarterKey[]>([]);
  const [historyOptions, setHistoryOptions] =
    useState<OptionsOrGroups<Option, GroupBase<Option>>>();
  const [quarterData, setQuarterData] = useState<
    Record<QuarterKey, RowWithDate>
  >({});
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<Option>();
  const [initialData, setInitialData] = useState<RowWithDate[]>([]);
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [loadingSubmission, setLoadingSubmission] = useState(false);
  const [initialOverrides, setInitialOverrides] = useState<OverriddenData[]>();
  const isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;

  const [data, setData] = useState(
    structuredClone(Object.values(INITIAL_PORTCO_DATA))
  );

  const mapVersionLabel = (timestamp: string) => {
    return new Date(timestamp)
      .toLocaleDateString('en-US', DATE_OPTIONS)
      .replace(' at', ',');
  };

  const loadPortCoData = useCallback(async () => {
    const { data: initialData, data_is_reviewed } =
      await getUserHistoricalCompsData();

    const defaultQuarter = initialData?.[0]?.COMPASS_DEFAULT_QUARTER;

    if (data_is_reviewed) {
      dispatch(setisDataReviewed(!!data_is_reviewed));
      const initialOverrides = await getUserHistoricalCompsDataOverrides();

      setHistoryOptions(
        initialOverrides.map(o => ({
          value: o.submission_timestamp,
          label: mapVersionLabel(o.submission_timestamp)
        }))
      );

      setInitialOverrides(initialOverrides);
    }

    const { rows, labels, quarterData } = transformToQuarterData(initialData);

    const defaultQuarterIdx = labels.findIndex(
      label =>
        label ===
        ((defaultQuarter as string) || '')?.replace('Q', 'q')?.replace(' ', '_')
    );

    dispatch(
      setDefaultQuarterIdx(
        defaultQuarterIdx === -1 ? labels.length - 1 : defaultQuarterIdx
      )
    );

    setData(rows);
    setLabels(labels);
    setLoadingData(false);
    setLoadingHistory(false);
    setInitialData(initialData);
    setQuarterData(quarterData);
    setReviewed(!!data_is_reviewed);
  }, [dispatch]);

  const handleResetData = useCallback(() => {
    const data = !selectedVersion
      ? initialData
      : initialData.map(d => ({
          ...d,
          ...(initialOverrides
            ?.find(o => o.submission_timestamp === selectedVersion.value)
            ?.overrides.find(
              o => o.QUARTER === d.QUARTER || o.PERIOD_END === d.PERIOD_END
            ) || {})
        }));

    const { rows } = transformToQuarterData(data);

    setData(rows);
    setFormDirty(false);
  }, [initialData, initialOverrides, selectedVersion]);

  const handleCloseErrorModal = () => {
    setIsErrorModalOpen(false);
  };

  useEffect(() => {
    loadPortCoData();
  }, [loadPortCoData]);

  useEffect(() => {
    handleResetData();
  }, [handleResetData]);

  const toggleAboutModal = () => {
    setIsAboutModalOpen(!isAboutModalOpen);
  };

  const handleSelectedVersion = (version: Option) => {
    setSelectedVersion(version);
  };

  const getRowValue = (data: Row[], colId: QuarterKey, rowId: TableKeys) => {
    return Number(data.find(row => row.id === rowId)?.[colId]);
  };

  const hasSubmitErrors = (
    data: Row[],
    updatedFields: Record<
      string,
      Partial<{
        id: TableKeys | ExpansionKeys | HeaderKeys | SeparatorKeys;
        title: string;
        titleExpanded?: string;
        [key: QuarterKey]: string | null | undefined;
      }>
    >
  ) => {
    let hasErrors = false;

    const hasErrorFields = Object.keys(updatedFields).some(key =>
      [
        TableKeys.GROSS_NEW_ARR,
        TableKeys.NET_NEW_ARR,
        TableKeys.ENDING_ARR,
        TableKeys.TOTAL_OPEX,
        TableKeys.TOTAL_HEADCOUNT
      ].includes(key as TableKeys)
    );

    if (!hasErrorFields) {
      return false;
    }

    const updatedFieldsKeys = Object.keys(updatedFields);

    if (updatedFieldsKeys.includes(TableKeys.GROSS_NEW_ARR)) {
      Object.keys(updatedFields[TableKeys.GROSS_NEW_ARR]).forEach(key => {
        const k = key as QuarterKey;
        const grossNewArr = getRowValue(data, k, TableKeys.GROSS_NEW_ARR);
        const newLogoArr = getRowValue(data, k, TableKeys.NEW_LOGO_ARR);
        const expansionArr = getRowValue(data, k, TableKeys.EXPANSION_ARR);

        if (
          (newLogoArr || expansionArr) &&
          grossNewArr.toFixed(3) !== (newLogoArr + expansionArr).toFixed(3)
        ) {
          hasErrors = true;
        }
      });
    }

    if (updatedFieldsKeys.includes(TableKeys.NET_NEW_ARR)) {
      Object.keys(updatedFields[TableKeys.NET_NEW_ARR]).forEach(key => {
        const k = key as QuarterKey;
        const netNewArr = getRowValue(data, k, TableKeys.NET_NEW_ARR);
        const grossNewArr = getRowValue(data, k, TableKeys.GROSS_NEW_ARR);
        const grossChurnArr = getRowValue(data, k, TableKeys.GROSS_CHURN_ARR);

        if (
          (grossNewArr || grossChurnArr) &&
          netNewArr.toFixed(3) !== (grossNewArr + grossChurnArr).toFixed(3)
        ) {
          hasErrors = true;
        }
      });
    }

    if (updatedFieldsKeys.includes(TableKeys.ENDING_ARR)) {
      Object.keys(updatedFields[TableKeys.ENDING_ARR]).forEach(key => {
        const k = key as QuarterKey;
        const endingArr = getRowValue(data, k, TableKeys.ENDING_ARR);
        const bopArr = getRowValue(data, k, TableKeys.BOP_ARR);
        const netNewArr = getRowValue(data, k, TableKeys.NET_NEW_ARR);

        if (
          (bopArr || netNewArr) &&
          endingArr.toFixed(3) !== (bopArr + netNewArr).toFixed(3)
        ) {
          hasErrors = true;
        }
      });
    }

    if (updatedFieldsKeys.includes(TableKeys.TOTAL_OPEX)) {
      Object.keys(updatedFields[TableKeys.TOTAL_OPEX]).forEach(key => {
        const k = key as QuarterKey;
        const totalOpex = getRowValue(data, k, TableKeys.TOTAL_OPEX);
        const opexSM = getRowValue(data, k, TableKeys.TOTAL_OPEX_SM);
        const opexRD = getRowValue(data, k, TableKeys.TOTAL_OPEX_RD);
        const opexGA = getRowValue(data, k, TableKeys.TOTAL_OPEX_GA);

        if (
          (opexSM || opexRD || opexGA) &&
          totalOpex.toFixed(3) !== (opexSM + opexRD + opexGA).toFixed(3)
        ) {
          hasErrors = true;
        }
      });
    }

    if (updatedFieldsKeys.includes(TableKeys.TOTAL_HEADCOUNT)) {
      Object.keys(updatedFields[TableKeys.TOTAL_HEADCOUNT]).forEach(key => {
        const k = key as QuarterKey;
        const totalHeadcount = getRowValue(data, k, TableKeys.TOTAL_HEADCOUNT);
        const headcountSM = getRowValue(data, k, TableKeys.TOTAL_HEADCOUNT_SM);
        const headcountRD = getRowValue(data, k, TableKeys.TOTAL_HEADCOUNT_RD);
        const headcountGA = getRowValue(data, k, TableKeys.TOTAL_HEADCOUNT_GA);

        if (
          (headcountSM || headcountRD || headcountGA) &&
          totalHeadcount.toFixed(3) !==
            (headcountSM + headcountRD + headcountGA).toFixed(3)
        ) {
          hasErrors = true;
        }
      });
    }

    return hasErrors;
  };

  const handleSubmit = async () => {
    setLoadingSubmission(true);
    setIsErrorModalOpen(false);
    const initialRows = transformToQuarterData(initialData).rows;

    const result = getOverrides(
      initialRows.map(({ meta, ...rest }: Row) => ({ ...rest })),
      data?.map(({ meta, ...rest }: Row) => ({ ...rest })),
      'id'
    );

    const mappedResult = mapOverrides(result, quarterData);

    const payload = !isReviewed
      ? { overrides: mappedResult, data_is_reviewed: true }
      : { overrides: mappedResult };

    await postUserHistoricalCompsDataOverrides(payload);

    setIsSubmitModalOpen(true);

    const overrides = await getUserHistoricalCompsDataOverrides();

    const mappedOverrides = overrides.map(o => ({
      value: o.submission_timestamp,
      label: mapVersionLabel(o.submission_timestamp)
    }));

    setInitialOverrides(overrides);
    setHistoryOptions(mappedOverrides);
    setSelectedVersion(mappedOverrides[0]);
    setLoadingSubmission(false);
  };

  const handleUpdateData = async () => {
    const initialRows = transformToQuarterData(initialData).rows;

    const result = getOverrides(
      initialRows.map(({ meta, ...rest }: Row) => ({ ...rest })),
      data?.map(({ meta, ...rest }: Row) => ({ ...rest })),
      'id'
    );

    const hasErrors = hasSubmitErrors(data, result);

    if (hasErrors) {
      setIsErrorModalOpen(true);
    } else {
      await handleSubmit();
    }
  };

  const handleNavigate = () => {
    if (isReviewed) {
      setIsSubmitModalOpen(false);
    } else {
      navigate('/');
    }
  };

  const dependents = useMemo(() => {
    const result = {} as Record<TableKeys, TableKeys[]>;

    for (const key of Object.keys(DEPENDENCIES)) {
      result[key as TableKeys] = [
        ...getDependents(key as TableKeys, DEPENDENCIES)
      ];
    }

    return result;
  }, []);

  const handleData = useCallback(
    (cell: CellContext<Row, keyof Row>, value: string | null | undefined) => {
      const selectedColId = cell.column.id as QuarterKey;
      const selectedRowId = cell.row.original.id as TableKeys;

      setFormDirty(true);

      startTransition(() =>
        setData((prev: Row[]) =>
          mapFormulas(
            prev,
            value === null ? null : Number(value) || 0,
            selectedRowId,
            selectedColId,
            dependents,
            labels,
            ENDING_ARR_ALL_DEPENDENCIES.includes(selectedRowId),
            value === null
          )
        )
      );
    },
    [labels, dependents]
  );

  const columns = useMemo(() => {
    const slicedLabels = labels.slice(
      defaultQuarterIdx - 7 < 0 ? 0 : defaultQuarterIdx - 7,
      defaultQuarterIdx + 1
    );

    return [
      getColumns('ARR FUNNEL', slicedLabels),
      getColumns('GROWTH & RETENTION', slicedLabels),
      getColumns('OPERATIONAL EFFICIENCY', slicedLabels),
      getColumns('PROFITABILITY AND CASH FLOW', slicedLabels),
      getColumns('HEADCOUNT', slicedLabels)
    ];
  }, [labels, defaultQuarterIdx]);

  const getValue = (
    rows: RowTanstack<Row>[],
    colId: QuarterKey,
    rowId: TableKeys
  ) => {
    return Number(
      (
        rows.find(row => row.original.id === rowId)
          ?.original as unknown as RowWithId
      )?.[colId]
    );
  };

  const hasError = useCallback(
    (cell: CellContext<Row, keyof Row>, isHighlighted: boolean) => {
      if (!isHighlighted) {
        return false;
      }

      const value = Number(cell.getValue());
      const rows = cell.table.getRowModel().rows;
      const colId = cell.column.id as QuarterKey;
      const cellId = (cell.row.original as { id: string }).id;

      if (cellId === TableKeys.GROSS_NEW_ARR) {
        const newLogoArr = getValue(rows, colId, TableKeys.NEW_LOGO_ARR);
        const expansionArr = getValue(rows, colId, TableKeys.EXPANSION_ARR);

        if (
          (newLogoArr || expansionArr) &&
          value.toFixed(3) !== (newLogoArr + expansionArr).toFixed(3)
        ) {
          return true;
        }
      }

      if (cellId === TableKeys.NET_NEW_ARR) {
        const grossNewArr = getValue(rows, colId, TableKeys.GROSS_NEW_ARR);
        const grossChurnArr = getValue(rows, colId, TableKeys.GROSS_CHURN_ARR);

        if (
          (grossNewArr || grossChurnArr) &&
          value.toFixed(3) !== (grossNewArr + grossChurnArr).toFixed(3)
        ) {
          return true;
        }
      }

      if (cellId === TableKeys.ENDING_ARR) {
        const bopArr = getValue(rows, colId, TableKeys.BOP_ARR);
        const netNewArr = getValue(rows, colId, TableKeys.NET_NEW_ARR);

        if (
          (bopArr || netNewArr) &&
          value.toFixed(3) !== (bopArr + netNewArr).toFixed(3)
        ) {
          return true;
        }
      }

      if (cellId === TableKeys.TOTAL_OPEX) {
        const opexSM = getValue(rows, colId, TableKeys.TOTAL_OPEX_SM);
        const opexRD = getValue(rows, colId, TableKeys.TOTAL_OPEX_RD);
        const opexGA = getValue(rows, colId, TableKeys.TOTAL_OPEX_GA);

        if (
          (opexSM || opexRD || opexGA) &&
          value.toFixed(3) !== (opexSM + opexRD + opexGA).toFixed(3)
        ) {
          return true;
        }
      }

      if (cellId === TableKeys.TOTAL_HEADCOUNT) {
        const headcountSM = getValue(rows, colId, TableKeys.TOTAL_HEADCOUNT_SM);
        const headcountRD = getValue(rows, colId, TableKeys.TOTAL_HEADCOUNT_RD);
        const headcountGA = getValue(rows, colId, TableKeys.TOTAL_HEADCOUNT_GA);

        if (
          (headcountSM || headcountRD || headcountGA) &&
          value.toFixed(3) !==
            (headcountSM + headcountRD + headcountGA).toFixed(3)
        ) {
          return true;
        }
      }

      return false;
    },
    []
  );

  const renderSection = (title: string, dataSlice: Row[], colIndex: number) => (
    <VerticalAccordion
      title={title}
      defaultExpand
      iconPosition='end'
      titleContainerClassName={`w-max self-start ${
        title === 'Key Financials' ? 'key-financials' : ''
      }`}
      titleClassName='py-2 !font-light !text-2xl'
      contentClassName='max-h-full'
      content={
        <EditableTable<Row, keyof Omit<Row, 'meta'>>
          data={dataSlice}
          hasError={hasError}
          handleData={handleData}
          columns={columns[colIndex]}
        />
      }
    />
  );

  return (
    <PortCoUserData
      data={data}
      isDarkMode={isDarkMode}
      isReviewed={isReviewed}
      isFormDirty={isFormDirty}
      initialData={initialData}
      loadingData={loadingData}
      handleSubmit={handleSubmit}
      renderSection={renderSection}
      handleNavigate={handleNavigate}
      historyOptions={historyOptions}
      loadingHistory={loadingHistory}
      handleResetData={handleResetData}
      selectedVersion={selectedVersion}
      isErrorModalOpen={isErrorModalOpen}
      isAboutModalOpen={isAboutModalOpen}
      toggleAboutModal={toggleAboutModal}
      handleUpdateData={handleUpdateData}
      isSubmitModalOpen={isSubmitModalOpen}
      loadingSubmission={loadingSubmission}
      handleCloseErrorModal={handleCloseErrorModal}
      handleSelectedVersion={handleSelectedVersion}
    />
  );
};

export default PortCoUserPage;
